/* eslint-disable react/no-array-index-key */
import { isValidElement, ReactNode } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

import ModuleTitle from './Title';
import { PDF_COLORS, PDF_FONT_FAMILY } from './styles';

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 30,
    paddingVertical: 6,
  },
  wrapper: {
    backgroundColor: PDF_COLORS.cardSurface,
    paddingVertical: 4,
  },
  text: {
    fontFamily: PDF_FONT_FAMILY,
    fontWeight: 400,
    fontSize: 12,
    color: PDF_COLORS.textPrimary,
    lineHeight: 1.4,
  },
  subtitle: {
    fontFamily: PDF_FONT_FAMILY,
    // Maps to "Subtitle 2 Inter Medium 14pt".
    fontWeight: 500,
    fontSize: 14,
    color: PDF_COLORS.textPrimary,
  },
  flexWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '15px',
  },
});

interface ModuleProps {
  title: string;
  content: Array<Array<[string, ReactNode]>>;
  contentBreak?: boolean;
  grid?: boolean;
}

const Module = ({ title, content, contentBreak, grid }: ModuleProps) => {
  const filteredArray = content
    .map((subArray) =>
      subArray.filter((item) => {
        const value = item[1];

        if (item === null) {
          return false;
        }

        if (typeof value !== 'string' && typeof value !== 'number' && !Array.isArray(value)) {
          return isValidElement(value);
        }

        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return typeof value === 'string' && value.length > 0;
      })
    )
    .filter((subr) => subr.length);

  if (!filteredArray.length) {
    return null;
  }

  return (
    <View style={styles.container} break={contentBreak}>
      {title !== '' && <ModuleTitle title={title} />}

      {filteredArray.map((part, i) => (
        <View
          style={[styles.wrapper, grid ? styles.flexWrapper : {}]}
          key={`module-content-${title}-${i}`}
        >
          {part.map(([subtitle, c], index) => (
            <View
              minPresenceAhead={50}
              key={`module-content-${title}-${i}-${index}`}
              style={[grid ? styles.flexWrapper : {}, { gap: '6px' }]}
            >
              {subtitle !== '' && (
                <Text style={[styles.subtitle, { marginTop: index && !grid ? 10 : 0 }]}>
                  {`${subtitle}${grid ? ':' : ''}`}
                </Text>
              )}

              {Array.isArray(c) &&
                c.length > 0 &&
                c.map((el) => (
                  <Text
                    style={[styles.text, { marginTop: 4 }]}
                    key={`module-content-${title}-${i}-${index}-${el}`}
                  >
                    {el}
                  </Text>
                ))}

              {typeof c === 'string' && (
                <Text style={[styles.text, { marginTop: subtitle !== '' && !grid ? 6 : 0 }]}>
                  {c}
                </Text>
              )}

              {typeof c !== 'string' && !Array.isArray(c) && c}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

export default Module;
